// EVO_FINANCE_STORE_SELECTOR_V1
import React, { useEffect, useState } from "react";
import API from "../../axiosInstance";

const euro = (value, digits = 2) =>
  Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

function Card({ label, value, note, highlight = false, precision = 2 }) {
  return (
    <div style={highlight ? styles.cardHighlight : styles.card}>
      <div style={styles.label}>{label}</div>
      <div style={highlight ? styles.valueHighlight : styles.value}>
        {euro(value, precision)} €
      </div>
      {note ? <div style={styles.note}>{note}</div> : null}
    </div>
  );
}

export default function AdminGainsWebPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/admin/gains")
      .then((res) => setData(res.data))
      .catch((err) => {
        console.error("Erreur Finance EVO :", err);
        setData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const purchases = data?.purchases || {};
  const gross = purchases?.gross || {};
  const fees = purchases?.estimated_store_fee || {};
  const net = purchases?.estimated_net_after_store_fee || {};

  const apple = data?.apple_purchases;
  const gift = data?.gift_commission || {};
  const creators = data?.creator_liabilities || {};
  const withdrawals = data?.withdrawals || {};
  const wallet = data?.platform_wallet || {};
  const summary = data?.finance_summary || {};

  return (
    <div style={styles.page}>
      <div style={styles.top}>
        <div>
          <div style={styles.kicker}>EVO LIVE ADMIN</div>
          <h1 style={styles.title}>💼 Finance EVO</h1>
          <p style={styles.subtitle}>
            Entrées, frais stores, commissions EVO et sommes dues aux créateurs.
          </p>
        </div>

        <button style={styles.backBtn} onClick={() => window.history.back()}>
          RETOUR
        </button>
      </div>

      {loading ? (
        <div style={styles.loading}>Chargement...</div>
      ) : !data ? (<div role="alert" style={styles.warningBox}>Chargement impossible. Recharge la page.</div>) : (
        <>
          <FinanceStoreSelector data={data} />

          <h2 style={styles.sectionTitle}>🎁 Commission EVO — cadeaux 50/50 — commune iOS et Android</h2>

          <div style={styles.grid}>
            <Card
              label="Commission EVO aujourd'hui"
              value={gift.today}
              precision={3}
            />

            <Card
              label="Commission EVO cette semaine"
              value={gift.week}
              precision={3}
            />

            <Card
              label="Commission EVO ce mois"
              value={gift.month}
              precision={3}
            />

            <Card
              label="Commission EVO totale"
              value={gift.total}
              precision={3}
              highlight
            />
          </div>

          <h2 style={styles.sectionTitle}>🏦 Situation actuelle — commune iOS et Android</h2>

          <div style={styles.grid}>
            <Card
              label="Portefeuille comptable EVO"
              value={wallet.balance}
              precision={3}
              note="Part EVO enregistrée sur les cadeaux"
            />

            <Card
              label="Revenus créateurs encore dus"
              value={creators.total}
              precision={3}
              note={`${creators.creators_with_balance || 0} créateur(s)`}
            />

            <Card
              label="Demandes de retrait en attente"
              value={withdrawals?.pending?.total}
              note={`${withdrawals?.pending?.count || 0} demande(s) à traiter`}
            />

            <Card
              label="Retraits approuvés non confirmés payés"
              value={withdrawals?.approved_not_confirmed_paid?.total}
              note={`${withdrawals?.approved_not_confirmed_paid?.count || 0} demande(s)`}
            />
          </div>

          <div style={styles.summaryGrid}>
            <div style={styles.creatorTotalBox}>
              <div style={styles.summaryLabel}>
                TOTAL À VERSER AUX CRÉATEURS
              </div>

              <div style={styles.creatorTotalValue}>
                {euro(summary.total_creators_to_cover)} €
              </div>

              <div style={styles.summaryNote}>
                Soldes créateurs + retraits en attente + retraits approuvés non confirmés payés
              </div>
            </div>

            <div style={styles.evoTotalBox}>
              <div style={styles.summaryLabel}>
                RESTE EVO ESTIMÉ
              </div>

              <div style={styles.evoTotalValue}>
                {euro(summary.evo_remaining_estimated)} €
              </div>

              <div style={styles.summaryNote}>
                Net Android + net Apple estimés, moins les sommes créateurs à couvrir. Calcul global aux deux plateformes.
              </div>
            </div>
          </div>


          <div style={styles.warningBox}>
            <strong>Important :</strong> Les frais Apple sont estimés au tarif standard de 30 %, à vérifier dans ton compte. Les montants Apple hors EUR peuvent correspondre au tarif EUR configuré, sans conversion réelle. Le net Google est une estimation
            avec 15 %. Le montant bancaire réel devra être confirmé plus tard
            avec les données de règlement Google Play. La commission cadeaux
            n'est pas ajoutée aux ventes de packs comme un deuxième revenu. Le reste EVO n'est pas un bénéfice ni une trésorerie bancaire : remboursements, taxes, paiements déjà effectués et autres charges restent à rapprocher des règlements réels.
          </div>
        </>
      )}
    </div>
  );
}


function FinanceStoreSelector({ data }) {
  const [store, setStore] = useState("total");
  const google = data?.purchases;
  const apple = data?.apple_purchases;

  const known = v =>
    v !== null && v !== undefined && v !== "" &&
    Number.isFinite(Number(v));

  const add = (a, b) =>
    known(a) && known(b) ? Number(a) + Number(b) : null;

  const money = v =>
    known(v) ? `${euro(v)} €` : "Indisponible";

  const amount = (key, period = "total") =>
    store === "android" ? google?.[key]?.[period] :
    store === "ios" ? apple?.[key]?.[period] :
    add(google?.[key]?.[period], apple?.[key]?.[period]);

  const title =
    store === "android" ? "Android — Google Play" :
    store === "ios" ? "iOS — Apple" :
    "Total Android + iOS";

  const count =
    store === "android" ? google?.count :
    store === "ios" ? apple?.count :
    add(google?.count, apple?.count);

  const rate = p =>
    known(p?.estimated_store_fee?.rate)
      ? `${Number(p.estimated_store_fee.rate) * 100} %`
      : "indisponible";

  const card = (label, value, note, highlight = false) => (
    <div style={highlight ? styles.cardHighlight : styles.card}>
      <div style={styles.label}>{label}</div>
      <div style={highlight ? styles.valueHighlight : styles.value}>
        {money(value)}
      </div>
      <div style={styles.note}>{note}</div>
    </div>
  );

  return (
    <>
      <label style={styles.label}>
        AFFICHER LES VENTES :{" "}
        <select
          value={store}
          onChange={e => setStore(e.target.value)}
          style={{
            background: "#0b2030", color: "#fff", padding: 12,
            border: "1px solid #00a5ba", borderRadius: 10,
            fontSize: 16
          }}
        >
          <option value="total">Total des deux</option>
          <option value="android">Android — Google Play</option>
          <option value="ios">iOS — Apple</option>
        </select>
      </label>

      <h2 style={styles.sectionTitle}>{title}</h2>

      <div style={styles.grid}>
        {card(
          "Achats bruts utilisateurs",
          amount("gross"),
          `${count ?? "—"} achat(s) validé(s)`
        )}

        {card(
          "Frais stores estimés",
          amount("estimated_store_fee"),
          store === "android"
            ? `Google : ${rate(google)}`
            : store === "ios"
              ? `Apple : ${rate(apple)}`
              : `Google : ${money(google?.estimated_store_fee?.total)} · Apple : ${money(apple?.estimated_store_fee?.total)}`
        )}

        {card(
          "Net estimé après frais stores",
          amount("estimated_net_after_store_fee"),
          "Achats moins frais estimés, pas un virement bancaire confirmé",
          true
        )}
      </div>

      <div style={styles.periodBox}>
        <div>Aujourd’hui : {money(amount("gross", "today"))}</div>
        <div>Cette semaine : {money(amount("gross", "week"))}</div>
        <div>Ce mois : {money(amount("gross", "month"))}</div>
      </div>

      {store === "total" && (
        <div style={{...styles.grid, marginTop: 16}}>
          {card(
            "Google — retenue estimée",
            google?.estimated_store_fee?.total,
            `${rate(google)} des ventes Android`
          )}
          {card(
            "Apple — retenue estimée",
            apple?.estimated_store_fee?.total,
            `${rate(apple)} des ventes iOS`
          )}
        </div>
      )}

      {!apple && store !== "android" && (
        <div role="alert" style={styles.warningBox}>
          Données Apple absentes de la réponse API.
          Le total des deux est indisponible.
        </div>
      )}

      <div style={styles.warningBox}>
        Le choix ci-dessus filtre les achats et les frais du store.
        Les commissions cadeaux, le portefeuille EVO, les soldes
        créateurs et les retraits restent détaillés ci-dessous.
        Ces montants sont communs aux deux plateformes :
        leur origine iOS/Android n’est pas enregistrée dans les tables actuelles.
        Le reste EVO en bas utilise le net des deux stores et déduit
        une seule fois le total à verser aux créateurs.
      </div>
    </>
  );
}


const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(135deg, #020617 0%, #07111f 48%, #041d2b 100%)",
    color: "#fff",
    padding: "32px",
  },

  top: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    borderBottom: "1px solid rgba(0,242,255,0.18)",
    paddingBottom: "22px",
    marginBottom: "26px",
  },

  kicker: {
    color: "#00f2ff",
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: "2px",
  },

  title: {
    fontSize: "32px",
    margin: "6px 0 0",
  },

  subtitle: {
    color: "#8fa2bd",
    fontSize: "14px",
  },

  backBtn: {
    background: "rgba(0,242,255,0.08)",
    color: "#00f2ff",
    border: "1px solid rgba(0,242,255,0.55)",
    borderRadius: "10px",
    padding: "10px 14px",
    fontWeight: 900,
    cursor: "pointer",
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "18px",
    marginBottom: "34px",
  },

  creatorTotalBox: {
    background: "rgba(7,17,31,0.98)",
    border: "1px solid rgba(255,209,102,0.55)",
    borderRadius: "22px",
    padding: "26px",
    boxShadow: "0 0 30px rgba(255,209,102,0.08)",
  },

  evoTotalBox: {
    background: "rgba(7,17,31,0.98)",
    border: "1px solid rgba(0,255,153,0.45)",
    borderRadius: "22px",
    padding: "26px",
    boxShadow: "0 0 30px rgba(0,255,153,0.08)",
  },

  summaryLabel: {
    color: "#8fa2bd",
    fontSize: "12px",
    fontWeight: 900,
    letterSpacing: "0.8px",
  },

  creatorTotalValue: {
    color: "#ffd166",
    fontSize: "38px",
    fontWeight: 900,
    marginTop: "12px",
  },

  evoTotalValue: {
    color: "#00ff99",
    fontSize: "38px",
    fontWeight: 900,
    marginTop: "12px",
  },

  summaryNote: {
    color: "#71849f",
    fontSize: "11px",
    lineHeight: 1.6,
    marginTop: "10px",
  },

  sectionTitle: {
    color: "#00f2ff",
    fontSize: "16px",
    marginTop: "30px",
    marginBottom: "14px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
  },

  card: {
    background: "rgba(7,17,31,0.94)",
    border: "1px solid rgba(0,242,255,0.22)",
    borderRadius: "18px",
    padding: "20px",
  },

  cardHighlight: {
    background: "rgba(7,17,31,0.98)",
    border: "1px solid rgba(255,209,102,0.45)",
    borderRadius: "18px",
    padding: "20px",
  },

  label: {
    color: "#8fa2bd",
    fontSize: "12px",
    fontWeight: 900,
  },

  value: {
    color: "#fff",
    fontSize: "26px",
    fontWeight: 900,
    marginTop: "10px",
  },

  valueHighlight: {
    color: "#ffd166",
    fontSize: "26px",
    fontWeight: 900,
    marginTop: "10px",
  },

  note: {
    color: "#71849f",
    fontSize: "11px",
    marginTop: "10px",
  },

  periodBox: {
    display: "flex",
    gap: "30px",
    flexWrap: "wrap",
    marginTop: "14px",
    padding: "14px",
    border: "1px solid rgba(0,242,255,0.15)",
    borderRadius: "12px",
    color: "#8fa2bd",
    fontSize: "12px",
  },

  futureBox: {
    border: "1px dashed rgba(0,242,255,0.25)",
    borderRadius: "16px",
    padding: "20px",
    color: "#8fa2bd",
    background: "rgba(7,17,31,0.6)",
  },

  warningBox: {
    marginTop: "30px",
    padding: "18px",
    borderRadius: "16px",
    border: "1px solid rgba(255,209,102,0.30)",
    color: "#aeb9c9",
    fontSize: "12px",
    lineHeight: 1.6,
  },

  loading: {
    padding: "30px",
    color: "#8fa2bd",
  },
};
